import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Globe, FileText, Bell,
  AlertTriangle, ChevronRight, Info, DollarSign, 
  Ship, Droplets, Wind, Activity, Mountain, Sun, Bug,
  ChevronDown, Filter, Factory, Anchor, ShieldCheck, ShieldAlert, Package, Shield, MapPin,
  ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { NavBar } from '@/components/NavBar';
import { RiskScoreLegend } from '@/components/RiskScoreLegend';
import { Line, Bar } from 'react-chartjs-2';
import { LineChart, Line as ReLine, ResponsiveContainer } from 'recharts';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '@/lib/api';
import { getRiskColor, getRiskLevel, getRiskBadgeClass, formatDateTime, getTrendIcon, getTrendColor, RISK_CATEGORIES_LABELS, getRiskBand, getRiskInterpretation, getDisruptionEstimate } from '@/lib/utils';
import { useUserProfile } from '@/lib/useUserProfile';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

const ROLE_LABELS = {
  'supply-chain': 'Supply chain',
  procurement: 'Procurement',
  risk: 'Risk & compliance',
  executive: 'Executive',
  operations: 'Trade & logistics',
  other: '',
};

const IMPORT_VALUE_MAP = {
  'under-1m': 500000,
  '1m-5m': 3000000,
  '5m-25m': 15000000,
  '25m-100m': 62500000,
  'over-100m': 150000000,
};

// Countries of concern for defence
const COUNTRIES_OF_CONCERN = ['CN', 'MM', 'KH', 'LA'];
const ALLIED_COUNTRIES = ['JP', 'KR', 'NZ', 'SG', 'IN'];
const VOLUME_WEIGHTS = { under_5: 0.05, '5_25': 0.15, '25_50': 0.375, over_50: 0.75, not_sure: 0.1 };

const DashboardPage = () => {
  const navigate = useNavigate();
  const { profile, hasProfile, audienceType, audienceLabel, isEnterprise, rolePrioritySignals, monitoredCountries, riskCategories, annualImportValue, tradeFootprint, homeCurrency, selectedPorts } = useUserProfile();
  const hasFootprint = hasProfile && Object.keys(tradeFootprint).length > 0;
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [trends, setTrends] = useState([]);
  const [sectorRisks, setSectorRisks] = useState([]);
  const [countries, setCountries] = useState([]);
  const [briefings, setBriefings] = useState([]);
  const [currencyPairs, setCurrencyPairs] = useState([]);
  const [activeDisasters, setActiveDisasters] = useState([]);
  const [freightLanes, setFreightLanes] = useState([]);
  const [portStatus, setPortStatus] = useState([]);
  const [tradeSnapshot, setTradeSnapshot] = useState(null);
  const [sanctionsDashboard, setSanctionsDashboard] = useState(null);
  const [mapView, setMapView] = useState('apac');
  const [footprintAlerts, setFootprintAlerts] = useState(false);
  const [execBriefing, setExecBriefing] = useState(null);
  const [execBriefingLoading, setExecBriefingLoading] = useState(false);
  const [riskDeltas, setRiskDeltas] = useState(null);
  const [portConditions, setPortConditions] = useState(null);
  const [disasterVisibleCount, setDisasterVisibleCount] = useState(5);
  const [disasterCountryFilter, setDisasterCountryFilter] = useState('all');
  const [disasterSeverityFilter, setDisasterSeverityFilter] = useState('all');
  const [disasterTypeFilter, setDisasterTypeFilter] = useState('all');
  const [disasterSort, setDisasterSort] = useState('date');

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, alertsRes, trendsRes, sectorsRes, countriesRes, briefingsRes, currencyRes, disasterRes, freightRes] = await Promise.all([
        api.getDashboardSummary(), api.getDashboardAlerts(), api.getRiskTrends(),
        api.getSectorRisks(), api.getCountries(), api.getBriefings(4),
        api.getCurrencyRates().catch(() => ({ data: { pairs: [] } })),
        api.getActiveDisasters().catch(() => ({ data: [] })),
        api.getFreightRates().catch(() => ({ data: { lanes: [] } })),
      ]);
      setSummary(summaryRes.data);
      setAlerts(alertsRes.data);
      setTrends(trendsRes.data);
      setSectorRisks(sectorsRes.data);
      setCountries(countriesRes.data);
      setBriefings(briefingsRes.data);
      setCurrencyPairs(currencyRes.data?.pairs || []);
      setActiveDisasters(Array.isArray(disasterRes.data) ? disasterRes.data : []);
      setFreightLanes(freightRes.data?.lanes || []);
      // Load new data sources (non-blocking)
      Promise.all([
        api.getPortStatus().catch(() => ({ data: [] })),
        api.getABSTradeSnapshot().catch(() => ({ data: null })),
        api.getSanctionsDashboard().catch(() => ({ data: null })),
        api.getRiskDeltas().catch(() => ({ data: null })),
        api.getPortConditionsSummary().catch(() => ({ data: null })),
      ]).then(([portsRes, absRes, sanctionsRes, deltasRes, portCondRes]) => {
        setPortStatus(Array.isArray(portsRes.data) ? portsRes.data : []);
        setTradeSnapshot(absRes.data);
        setSanctionsDashboard(sanctionsRes.data);
        setRiskDeltas(deltasRes.data);
        setPortConditions(portCondRes.data);
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Personalisation logic ──

  // Prioritised countries: user's selected countries first
  const sortedCountries = useMemo(() => {
    if (!monitoredCountries.length) return countries;
    return [...countries].sort((a, b) => {
      const aMatch = monitoredCountries.includes(a.country_code) ? 0 : 1;
      const bMatch = monitoredCountries.includes(b.country_code) ? 0 : 1;
      return aMatch - bMatch || b.overall_risk_score - a.overall_risk_score;
    });
  }, [countries, monitoredCountries]);

  // Top countries risk cards (user's monitored countries)
  const topCountries = useMemo(() => {
    if (!monitoredCountries.length) return countries.slice(0, 5);
    return sortedCountries.filter(c => monitoredCountries.includes(c.country_code)).slice(0, isEnterprise ? 8 : 5);
  }, [sortedCountries, monitoredCountries, isEnterprise, countries]);

  // Sorted alerts: prioritise user's countries + role signals
  const sortedAlerts = useMemo(() => {
    if (!hasProfile) return alerts;
    return [...alerts].sort((a, b) => {
      const aCountry = monitoredCountries.includes(a.country_code) ? 0 : 1;
      const bCountry = monitoredCountries.includes(b.country_code) ? 0 : 1;
      if (aCountry !== bCountry) return aCountry - bCountry;
      const aRole = rolePrioritySignals.includes(a.signal_type) ? 0 : 1;
      const bRole = rolePrioritySignals.includes(b.signal_type) ? 0 : 1;
      if (aRole !== bRole) return aRole - bRole;
      const aCat = riskCategories.includes(a.signal_type) ? 0 : 1;
      const bCat = riskCategories.includes(b.signal_type) ? 0 : 1;
      return aCat - bCat;
    });
  }, [alerts, hasProfile, monitoredCountries, rolePrioritySignals, riskCategories]);

  // Sorted sector risks: user's sector first
  const sortedSectors = useMemo(() => {
    if (!audienceLabel) return sectorRisks;
    return [...sectorRisks].sort((a, b) => {
      if (a.sector_name === audienceLabel) return -1;
      if (b.sector_name === audienceLabel) return 1;
      return b.risk_score - a.risk_score;
    });
  }, [sectorRisks, audienceLabel]);

  // Role-based dashboard subtitle
  const roleSubtitle = useMemo(() => {
    if (audienceType === 'importer') return 'Freight, currency, and supplier risk signals are prioritised for you.';
    if (audienceType === 'government') return 'Sovereign risk and compliance signals are prioritised for you.';
    if (audienceType === 'defence') return 'AUKUS compliance and country-of-concern signals are prioritised for you.';
    const role = profile?.primary_role;
    if (role === 'supply-chain' || role === 'procurement') return 'Supplier risk and freight disruption signals are prioritised for you.';
    if (role === 'risk') return 'Regulatory and compliance signals are prioritised for you.';
    if (role === 'executive') return 'High-level risk trends and summaries are prioritised for you.';
    if (role === 'operations') return 'Tariff, customs, and logistics signals are prioritised for you.';
    return null;
  }, [audienceType, profile?.primary_role]);

  // Fetch executive briefing when footprint data is available
  const fetchExecBriefing = async () => {
    if (!hasFootprint || Object.keys(tradeFootprint).length === 0) return;
    setExecBriefingLoading(true);
    try {
      const res = await api.getExecutiveBriefing({
        trade_footprint: tradeFootprint,
        company_name: profile?.company_name || null,
      });
      setExecBriefing(res.data);
    } catch (e) { console.error('Executive briefing error:', e); }
    finally { setExecBriefingLoading(false); }
  };

  useEffect(() => {
    if (hasFootprint && !execBriefing && !execBriefingLoading) {
      fetchExecBriefing();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFootprint]);

  // Filtered and sorted disasters
  const filteredDisasters = useMemo(() => {
    let result = [...activeDisasters];
    if (disasterCountryFilter !== 'all') result = result.filter(d => d.country_code === disasterCountryFilter);
    if (disasterSeverityFilter !== 'all') result = result.filter(d => d.severity === disasterSeverityFilter);
    if (disasterTypeFilter !== 'all') result = result.filter(d => d.types?.includes(disasterTypeFilter));
    if (disasterSort === 'severity') {
      const order = { CRITICAL: 0, HIGH: 1, MODERATE: 2, LOW: 3 };
      result.sort((a, b) => (order[a.severity] ?? 3) - (order[b.severity] ?? 3));
    } else if (disasterSort === 'country') {
      result.sort((a, b) => (a.country_name || '').localeCompare(b.country_name || ''));
    }
    // default: already sorted by date from API
    return result;
  }, [activeDisasters, disasterCountryFilter, disasterSeverityFilter, disasterTypeFilter, disasterSort]);

  const disasterCountries = useMemo(() => {
    const set = new Map();
    activeDisasters.forEach(d => { if (d.country_code) set.set(d.country_code, d.country_name); });
    return Array.from(set.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [activeDisasters]);

  const disasterEventTypes = useMemo(() => {
    const set = new Set();
    activeDisasters.forEach(d => d.types?.forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [activeDisasters]);

  // ── Trade Footprint Calculations ──

  const footprintRiskScore = useMemo(() => {
    if (!hasFootprint || !countries.length) return null;
    let totalWeight = 0;
    let weightedSum = 0;
    Object.entries(tradeFootprint).forEach(([code, fp]) => {
      const country = countries.find(c => c.country_code === code);
      if (!country) return;
      const w = VOLUME_WEIGHTS[fp.volume] || 0.1;
      weightedSum += country.overall_risk_score * w;
      totalWeight += w;
    });
    return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : null;
  }, [hasFootprint, tradeFootprint, countries]);

  const footprintExposure = useMemo(() => {
    if (!hasFootprint || !countries.length) return [];
    return Object.entries(tradeFootprint)
      .map(([code, fp]) => {
        const country = countries.find(c => c.country_code === code);
        if (!country) return null;
        return { ...country, volume: fp.volume, direction: fp.direction };
      })
      .filter(Boolean)
      .sort((a, b) => b.overall_risk_score - a.overall_risk_score)
      .slice(0, 3);
  }, [hasFootprint, tradeFootprint, countries]);

  // Map view configuration
  const mapConfig = useMemo(() => {
    if (mapView === 'global') return { center: [20, 0], zoom: 2 };
    if (mapView === 'footprint' && hasFootprint) {
      const fpCountries = countries.filter(c => tradeFootprint[c.country_code]);
      if (fpCountries.length > 0) {
        const lats = fpCountries.map(c => c.coordinates[1]);
        const lngs = fpCountries.map(c => c.coordinates[0]);
        return { center: [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2], zoom: 3 };
      }
    }
    return { center: [15, 105], zoom: 3 };
  }, [mapView, hasFootprint, tradeFootprint, countries]);

  const mapCountries = useMemo(() => {
    if (mapView === 'footprint' && hasFootprint) {
      return countries.filter(c => tradeFootprint[c.country_code]);
    }
    if (mapView === 'apac') {
      return countries.filter(c => !c.region || c.region === 'apac');
    }
    return countries;
  }, [mapView, hasFootprint, tradeFootprint, countries]);

  // Filtered alerts with footprint toggle + country prioritisation
  const displayAlerts = useMemo(() => {
    if (footprintAlerts && hasFootprint) {
      const fpCodes = Object.keys(tradeFootprint);
      return sortedAlerts.filter(a => fpCodes.includes(a.country_code));
    }
    // Auto-prioritise monitored countries for all users
    if (hasProfile && monitoredCountries.length > 0) {
      const monitored = sortedAlerts.filter(a => monitoredCountries.includes(a.country_code));
      const other = sortedAlerts.filter(a => !monitoredCountries.includes(a.country_code));
      return [...monitored, ...other];
    }
    return sortedAlerts;
  }, [sortedAlerts, footprintAlerts, hasFootprint, tradeFootprint, hasProfile, monitoredCountries]);

  // Memoised port status for dashboard widget
  const dashboardPorts = useMemo(() => {
    return portStatus.filter(p => {
      if (selectedPorts.length > 0) return true;
      return p.country_code !== 'AU';
    }).sort((a, b) => {
      const aSelected = selectedPorts.includes(a.port_id);
      const bSelected = selectedPorts.includes(b.port_id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    }).slice(0, 8);
  }, [portStatus, selectedPorts]);

  // Charts
  const trendChartData = {
    labels: trends.map(t => t.month),
    datasets: [
      {
        label: 'Risk Score',
        data: trends.map(t => t.score),
        borderColor: '#0097A7',
        backgroundColor: 'rgba(77, 208, 225, 0.15)',
        fill: true,
        tension: 0.35,
        pointBackgroundColor: trends.map((_, i) => i === trends.length - 1 ? '#0097A7' : '#fff'),
        pointBorderColor: '#0097A7',
        pointBorderWidth: 2,
        pointRadius: trends.map((_, i) => i === trends.length - 1 ? 7 : 4),
        pointHoverRadius: 7,
      },
      {
        label: 'Upper band',
        data: trends.map(t => t.confidence_high),
        borderColor: 'transparent',
        backgroundColor: 'rgba(77, 208, 225, 0.08)',
        fill: '+1',
        tension: 0.35,
        pointRadius: 0,
      },
      {
        label: 'Lower band',
        data: trends.map(t => t.confidence_low),
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.35,
        pointRadius: 0,
      }
    ]
  };

  // Custom plugin to show score label on the last data point
  const lastPointLabelPlugin = {
    id: 'lastPointLabel',
    afterDatasetsDraw(chart) {
      const ds = chart.getDatasetMeta(0);
      if (!ds || !ds.data.length) return;
      const last = ds.data[ds.data.length - 1];
      const val = chart.data.datasets[0].data[chart.data.datasets[0].data.length - 1];
      const ctx = chart.ctx;
      ctx.save();
      ctx.font = 'bold 12px DM Sans, sans-serif';
      ctx.fillStyle = '#0D3B4F';
      ctx.textAlign = 'center';
      ctx.fillText(val, last.x, last.y - 14);
      ctx.restore();
    }
  };

  const trendChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#0A2533',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        cornerRadius: 8,
        filter: (item) => item.datasetIndex === 0,
        callbacks: { label: (ctx) => `Risk score: ${ctx.raw}` }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#4E6974', font: { family: 'DM Sans', size: 11 } }
      },
      y: {
        min: 25,
        max: 65,
        grid: { color: 'rgba(0,0,0,0.05)' },
        ticks: { color: '#4E6974', font: { family: 'DM Sans', size: 11 } }
      }
    }
  };

  const sectorChartData = {
    labels: sortedSectors.map(s => s.sector_name),
    datasets: [{ data: sortedSectors.map(s => s.risk_score), backgroundColor: sortedSectors.map(s => s.sector_name === audienceLabel ? '#0097A7' : getRiskColor(s.risk_score)), borderRadius: 4, barThickness: 28 }]
  };
  const sectorChartOptions = { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { backgroundColor: '#0A2533', titleColor: '#fff', bodyColor: '#fff', padding: 12, cornerRadius: 8 } }, scales: { x: { max: 100, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { color: '#4E6974', font: { family: 'DM Sans' } } }, y: { grid: { display: false }, ticks: { color: '#0D3B4F', font: { family: 'DM Sans', weight: 500 } } } } };

  const companyName = profile?.company_name;

  const disasterIcon = (iconName) => {
    const map = { droplets: Droplets, wind: Wind, activity: Activity, mountain: Mountain, sun: Sun, bug: Bug };
    const Icon = map[iconName] || AlertTriangle;
    return <Icon className="h-5 w-5" />;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Navigation */}
      <NavBar />

      <main className="pt-16 px-4 lg:px-6 pb-8" data-testid="dashboard-main">
        <div className="max-w-[1600px] mx-auto">
          {/* Personalisation banner */}
          {!hasProfile && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-3 rounded-lg bg-[#E0F7FA] border border-[#4DD0E1]/30 flex items-center justify-between" data-testid="personalise-banner">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-[#0097A7]" />
                <span className="text-sm text-[#0D3B4F]">Personalise your dashboard to see the countries, sectors, and risk signals that matter most to you.</span>
              </div>
              <Link to="/onboarding" className="text-[#0097A7] text-sm font-medium hover:underline whitespace-nowrap ml-4" data-testid="personalise-link">Set up now</Link>
            </motion.div>
          )}

          {/* Welcome header */}
          {hasProfile && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 mb-2">
              <h2 className="text-xl font-bold text-[#0D3B4F]" data-testid="welcome-header">
                Welcome back, {companyName}
              </h2>
              {roleSubtitle && <p className="text-sm text-[#4E6974] mt-1">{roleSubtitle}</p>}
            </motion.div>
          )}

          {/* AI Executive Briefing */}
          {hasFootprint && (execBriefing || execBriefingLoading) && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3" data-testid="executive-briefing-card">
              <Card className="bg-gradient-to-r from-[#0D3B4F] to-[#0A2533] rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.12)] border-none overflow-hidden">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="w-8 h-8 rounded-lg bg-[#0097A7]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Activity className="h-4 w-4 text-[#4DD0E1]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-[#4DD0E1] uppercase tracking-wider">Morning briefing</span>
                          {execBriefing?.source === 'ai' && (
                            <Badge className="bg-[#4DD0E1]/15 text-[#4DD0E1] text-[9px] border-none">AI</Badge>
                          )}
                        </div>
                        {execBriefingLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-[#4DD0E1] animate-pulse" />
                            <div className="h-1.5 w-1.5 rounded-full bg-[#4DD0E1] animate-pulse" style={{ animationDelay: '150ms' }} />
                            <div className="h-1.5 w-1.5 rounded-full bg-[#4DD0E1] animate-pulse" style={{ animationDelay: '300ms' }} />
                            <span className="text-sm text-[#4E6974] ml-1">Generating your briefing...</span>
                          </div>
                        ) : (
                          <p className="text-sm text-white/90 leading-relaxed" data-testid="executive-briefing-text">
                            {execBriefing?.briefing}
                          </p>
                        )}
                        {execBriefing && !execBriefingLoading && (
                          <div className="flex items-center gap-3 mt-3">
                            {execBriefing.highest_risk_country && (
                              <span className="text-[10px] text-[#4E6974]">
                                Highest risk: {execBriefing.highest_risk_country} ({execBriefing.highest_risk_score}/100) · {execBriefing.footprint_count} countries tracked
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <button onClick={fetchExecBriefing} disabled={execBriefingLoading}
                      className="text-[#4E6974] hover:text-[#4DD0E1] transition-colors flex-shrink-0 p-1"
                      data-testid="refresh-briefing-btn" title="Refresh briefing">
                      <Activity className={`h-4 w-4 ${execBriefingLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* What Changed Overnight — Risk Delta Card */}
          {riskDeltas && riskDeltas.deltas && riskDeltas.deltas.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3" data-testid="risk-deltas-card">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-md bg-[#0D3B4F] flex items-center justify-center">
                        <Activity className="h-3.5 w-3.5 text-[#4DD0E1]" />
                      </div>
                      <span className="text-sm font-bold text-[#0D3B4F]">What changed overnight</span>
                      <span className="text-[10px] text-[#4E6974]">{riskDeltas.total_movers} {riskDeltas.total_movers === 1 ? 'country' : 'countries'} moved 3+ points</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {riskDeltas.deltas.slice(0, 8).map(d => {
                      const isFootprintCountry = hasFootprint && tradeFootprint[d.country_code];
                      return (
                        <div key={d.country_code}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors cursor-pointer ${isFootprintCountry ? 'bg-[#0D3B4F]/5 border border-[#0D3B4F]/10' : 'bg-[#F5F5F5]'} hover:bg-[#EEEEEE]`}
                          onClick={() => navigate(`/countries/${d.country_code}`)}
                          data-testid={`delta-${d.country_code.toLowerCase()}`}>
                          <span className="text-base">{d.flag_emoji}</span>
                          <span className="text-xs font-medium text-[#0D3B4F]">{d.country_name}</span>
                          <span className="text-xs font-bold text-[#0D3B4F]">{d.current_score}</span>
                          <span className={`text-xs font-bold flex items-center gap-0.5 ${d.direction === 'up' ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}`}>
                            {d.direction === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                            {Math.abs(d.change)}
                          </span>
                          {isFootprintCountry && <div className="w-1.5 h-1.5 rounded-full bg-[#FF9500]" title="In your trade footprint" />}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
          {riskDeltas && riskDeltas.deltas && riskDeltas.deltas.length === 0 && hasProfile && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3" data-testid="risk-deltas-stable">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-6 h-6 rounded-md bg-[#2E7D32]/10 flex items-center justify-center">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#2E7D32]" />
                  </div>
                  <span className="text-sm text-[#4E6974]">Stable overnight — no countries moved 3+ risk points</span>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Audience-specific context banner */}
          {hasProfile && audienceType === 'importer' && currencyPairs.length > 0 && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3" data-testid="importer-cost-banner">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#0097A7]">
                <CardContent className="p-4 flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-[#0097A7] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#0D3B4F]">
                      Estimated import cost movement this month: {(() => {
                        const avgChange = currencyPairs.reduce((sum, p) => sum + (p.change_30d_pct || 0), 0) / (currencyPairs.length || 1);
                        const baseValue = IMPORT_VALUE_MAP[annualImportValue] || 5000000;
                        const monthlyImpact = Math.round(baseValue / 12 * Math.abs(avgChange) / 100);
                        return `${avgChange >= 0 ? '+' : '-'}$${monthlyImpact.toLocaleString()} based on currency and freight changes`;
                      })()}
                    </p>
                    <p className="text-xs text-[#4E6974] mt-0.5">Based on AUD movements across your sourcing currencies</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {hasProfile && audienceType === 'government' && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3" data-testid="gov-compliance-banner">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#0097A7]">
                <CardContent className="p-4 flex items-center gap-3">
                  <Globe className="h-5 w-5 text-[#0097A7] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#0D3B4F]">
                      Compliance snapshot: {(() => {
                        const monitored = monitoredCountries.length;
                        const elevated = countries.filter(c => monitoredCountries.includes(c.country_code) && c.overall_risk_score >= 60).length;
                        const safe = monitored - elevated;
                        return `${safe} of ${monitored} monitored countries meet sovereign risk thresholds. ${elevated} ${elevated === 1 ? 'country requires' : 'countries require'} review.`;
                      })()}
                    </p>
                    <p className="text-xs text-[#4E6974] mt-0.5">Based on real-time sovereign risk scoring across your supplier panel countries</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {hasProfile && audienceType === 'defence' && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mt-3" data-testid="defence-sovereignty-banner">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#FF9500]">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#FF9500] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-[#0D3B4F]">
                      Supply chain sovereignty: {(() => {
                        const total = monitoredCountries.length || 1;
                        const allied = monitoredCountries.filter(c => ALLIED_COUNTRIES.includes(c)).length;
                        const concern = monitoredCountries.filter(c => COUNTRIES_OF_CONCERN.includes(c)).length;
                        const alliedPct = Math.round((allied / total) * 100);
                        return `${alliedPct}% of your mapped supply chain is sourced from allied nations. ${concern} ${concern === 1 ? 'supplier country' : 'supplier countries'} flagged for country-of-concern exposure.`;
                      })()}
                    </p>
                    <p className="text-xs text-[#4E6974] mt-0.5">AUKUS compliance and sovereign risk monitoring active</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Supply Chain at a Glance (top priority) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#4E6974] text-sm font-medium">Total suppliers</span>
                    <Factory className="h-4 w-4 text-[#0097A7]" />
                  </div>
                  <div className="text-4xl font-bold text-[#0D3B4F]" data-testid="total-suppliers-count">{tradeSnapshot?.supplier_count || 0}</div>
                  <p className="text-[#4E6974] text-sm mt-2">across {tradeSnapshot?.countries?.length || 0} countries</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#4E6974] text-sm font-medium">{hasFootprint ? 'Trade footprint risk' : 'Average risk score'}</span>
                    <div className={`flex items-center gap-1 text-sm ${getTrendColor(summary?.risk_trend)}`}>
                      {getTrendIcon(summary?.risk_trend)}
                      <span>{summary?.trend_change || 0}%</span>
                    </div>
                  </div>
                  <div className="flex items-end gap-2">
                    <span className="text-4xl font-bold text-[#0D3B4F]" data-testid="overall-risk-score">{hasFootprint && footprintRiskScore !== null ? footprintRiskScore : (summary?.overall_risk_score || 0)}</span>
                    <span className="text-[#4E6974] text-lg mb-1">/100</span>
                  </div>
                  <div className="mt-3 h-2 bg-[#F5F5F5] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${hasFootprint && footprintRiskScore !== null ? footprintRiskScore : (summary?.overall_risk_score || 0)}%`, backgroundColor: getRiskColor(hasFootprint && footprintRiskScore !== null ? footprintRiskScore : (summary?.overall_risk_score || 0)) }} />
                  </div>
                  {hasFootprint && <p className="text-[10px] text-[#4E6974] mt-2">Weighted by your trade volume bands</p>}
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#4E6974] text-sm font-medium">Total spend at risk</span>
                    <DollarSign className="h-4 w-4 text-[#FF9500]" />
                  </div>
                  <div className="text-4xl font-bold text-[#0D3B4F]" data-testid="spend-at-risk">
                    ${tradeSnapshot?.total_from_suppliers_aud ? (tradeSnapshot.total_from_suppliers_aud / 1e9).toFixed(0) + 'B' : '—'}
                  </div>
                  <p className="text-[#4E6974] text-sm mt-2">{countries.filter(c => c.overall_risk_score >= 60).length} countries with elevated risk</p>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[#4E6974] text-sm font-medium">Concentration risk</span>
                    <AlertTriangle className="h-4 w-4 text-[#FF9500]" />
                  </div>
                  <div className="text-4xl font-bold text-[#0D3B4F]" data-testid="concentration-risk">
                    {tradeSnapshot?.countries?.length > 0
                      ? `${Math.round((tradeSnapshot.countries[0]?.imports_aud || 0) / Math.max(tradeSnapshot.total_from_suppliers_aud || 1, 1) * 100)}%`
                      : '—'}
                  </div>
                  <p className="text-[#4E6974] text-sm mt-2">
                    {tradeSnapshot?.countries?.[0]?.country_name || 'Top country'} share of imports
                  </p>
                  <Link to="/concentration" className="text-[#0097A7] text-xs hover:underline mt-2 inline-block">View full analysis</Link>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Trade Footprint Exposure Card */}
          {hasFootprint && footprintExposure.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28 }} className="mt-4" data-testid="footprint-exposure-card">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#FF9500]">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-[#FF9500]" />
                    <span className="text-sm font-bold text-[#0D3B4F]">Trade footprint exposure</span>
                    <span className="text-[10px] text-[#4E6974]">Top high-risk countries in your footprint</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {footprintExposure.map(c => (
                      <div key={c.country_code} className="flex items-center gap-3 p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] cursor-pointer transition-colors"
                        onClick={() => navigate(`/countries/${c.country_code}`)} data-testid={`fp-exposure-${c.country_code.toLowerCase()}`}>
                        <span className="text-xl">{c.flag_emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#0D3B4F] truncate">{c.country_name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs font-bold" style={{ color: getRiskColor(c.overall_risk_score) }}>{c.overall_risk_score}</span>
                            <span className="text-[10px] text-[#4E6974]">{(c.volume || '').replace('_', '-')} of trade</span>
                          </div>
                        </div>
                        <Badge className={`${getRiskBadgeClass(c.overall_risk_score >= 60 ? 'CRITICAL' : c.overall_risk_score >= 40 ? 'HIGH' : 'MODERATE')} text-[9px]`}>
                          {c.overall_risk_score >= 60 ? 'High' : c.overall_risk_score >= 40 ? 'Elevated' : 'Moderate'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Port Conditions Card */}
          {portConditions && portConditions.total_ports > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.29 }} className="mt-4" data-testid="port-conditions-card">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Anchor className="h-5 w-5 text-[#0097A7]" />
                      <div>
                        <span className="text-sm font-bold text-[#0D3B4F]">Port conditions</span>
                        <div className="flex items-center gap-2 mt-0.5 text-xs">
                          <span className="text-[#2E7D32] font-medium">{portConditions.status_counts?.Normal || 0} normal</span>
                          {(portConditions.status_counts?.Delays || 0) > 0 && <span className="text-[#FF9500] font-medium">{portConditions.status_counts.Delays} delays</span>}
                          {portConditions.alert_count > 0 && <span className="text-[#FF3B30] font-medium">{portConditions.alert_count} congested</span>}
                        </div>
                      </div>
                    </div>
                    <Link to="/ports-routes" className="text-[#0097A7] text-xs hover:underline" data-testid="port-conditions-link">View ports & routes</Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* AUKUS Compliance Dashboard Panel (Defence users) */}
          {hasProfile && audienceType === 'defence' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-6" data-testid="aukus-compliance-panel">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                    <Shield className="h-5 w-5 text-[#FF9500]" /> AUKUS supply chain compliance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Sovereignty Percentage */}
                    <div className="p-4 rounded-lg bg-[#F5F5F5]">
                      <p className="text-xs text-[#4E6974] uppercase tracking-wide mb-2">Allied nation sourcing</p>
                      {(() => {
                        const total = monitoredCountries.length || 1;
                        const allied = monitoredCountries.filter(c => ALLIED_COUNTRIES.includes(c)).length;
                        const alliedPct = Math.round((allied / total) * 100);
                        return (
                          <>
                            <div className="flex items-end gap-1 mb-2">
                              <span className={`text-3xl font-bold ${alliedPct >= 70 ? 'text-[#2E7D32]' : alliedPct >= 50 ? 'text-[#FF9500]' : 'text-[#FF3B30]'}`}>{alliedPct}%</span>
                              <span className="text-sm text-[#4E6974] mb-0.5">of supply chain</span>
                            </div>
                            <div className="h-2.5 bg-[#E0E0E0] rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-[#2E7D32]" style={{ width: `${alliedPct}%` }} />
                            </div>
                            <p className="text-xs text-[#4E6974] mt-2">AUKUS-aligned: AU, UK, US, JP, KR, SG, NZ</p>
                          </>
                        );
                      })()}
                    </div>
                    {/* Countries of Concern */}
                    <div className="p-4 rounded-lg bg-[#F5F5F5]">
                      <p className="text-xs text-[#4E6974] uppercase tracking-wide mb-2">Countries of concern</p>
                      {(() => {
                        const concern = monitoredCountries.filter(c => COUNTRIES_OF_CONCERN.includes(c));
                        return (
                          <>
                            <div className="flex items-end gap-1 mb-3">
                              <span className={`text-3xl font-bold ${concern.length === 0 ? 'text-[#2E7D32]' : 'text-[#FF3B30]'}`}>{concern.length}</span>
                              <span className="text-sm text-[#4E6974] mb-0.5">flagged</span>
                            </div>
                            <div className="space-y-1.5">
                              {concern.map(cc => {
                                const c = countries.find(x => x.country_code === cc);
                                return c ? (
                                  <div key={cc} className="flex items-center justify-between">
                                    <span className="text-sm text-[#0D3B4F] flex items-center gap-1.5">
                                      <AlertTriangle className="h-3 w-3 text-[#FF3B30]" />{c.country_name}
                                    </span>
                                    <Badge className="bg-[#FF3B30]/10 text-[#FF3B30] text-[10px]">{c.overall_risk_score}</Badge>
                                  </div>
                                ) : null;
                              })}
                              {concern.length === 0 && <p className="text-sm text-[#2E7D32]">No country-of-concern exposure</p>}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {/* Sovereignty Risk Score */}
                    <div className="p-4 rounded-lg bg-[#F5F5F5]">
                      <p className="text-xs text-[#4E6974] uppercase tracking-wide mb-2">Sovereignty risk score</p>
                      {(() => {
                        const concern = monitoredCountries.filter(c => COUNTRIES_OF_CONCERN.includes(c));
                        const sovScore = Math.min(100, concern.length * 25 + (concern.includes('CN') ? 20 : 0));
                        return (
                          <>
                            <div className="flex items-end gap-1 mb-2">
                              <span className={`text-3xl font-bold ${sovScore < 30 ? 'text-[#2E7D32]' : sovScore < 60 ? 'text-[#FF9500]' : 'text-[#FF3B30]'}`}>{sovScore}</span>
                              <span className="text-sm text-[#4E6974] mb-0.5">/100</span>
                            </div>
                            <div className="h-2.5 bg-[#E0E0E0] rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${sovScore}%`, backgroundColor: sovScore < 30 ? '#2E7D32' : sovScore < 60 ? '#FF9500' : '#FF3B30' }} />
                            </div>
                            <p className="text-xs text-[#4E6974] mt-2">Separate from commercial risk — measures dependency on non-allied nations</p>
                            <Link to="/aukus" className="text-[#0097A7] text-xs hover:underline mt-2 inline-block">View full AUKUS compliance page</Link>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Map + Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="lg:col-span-3">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-[450px]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F]">
                      {mapView === 'apac' ? 'Regional risk heat map' : mapView === 'global' ? 'Global risk heat map' : 'Trade footprint map'}
                    </CardTitle>
                    <div className="flex bg-[#F5F5F5] rounded-lg p-0.5" data-testid="map-view-toggle">
                      {[
                        { key: 'apac', label: 'Regional' },
                        { key: 'global', label: 'Global' },
                        ...(hasFootprint ? [{ key: 'footprint', label: 'My footprint' }] : []),
                      ].map(v => (
                        <button key={v.key} onClick={() => setMapView(v.key)}
                          className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${mapView === v.key ? 'bg-[#00707C] text-white shadow-sm' : 'text-[#4E6974] hover:text-[#0D3B4F]'}`}
                          data-testid={`map-toggle-${v.key}`}>
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0 h-[380px]">
                  <MapContainer key={mapView} center={mapConfig.center} zoom={mapConfig.zoom} scrollWheelZoom={true} style={{ height: '100%', width: '100%', borderRadius: '0 0 8px 8px' }} className="map-container">
                    <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                    {mapCountries.map((country) => {
                      const isMonitored = hasProfile && monitoredCountries.includes(country.country_code);
                      const isFootprint = hasFootprint && tradeFootprint[country.country_code];
                      const fpVolume = isFootprint ? tradeFootprint[country.country_code].volume : null;
                      const volumeScale = fpVolume === 'over_50' ? 1.5 : fpVolume === '25_50' ? 1.3 : fpVolume === '5_25' ? 1.1 : 1;
                      return (
                        <CircleMarker
                          key={country.country_code}
                          center={[country.coordinates[1], country.coordinates[0]]}
                          radius={(isFootprint ? Math.max(18, country.overall_risk_score / 3) * volumeScale : isMonitored ? Math.max(16, country.overall_risk_score / 3.5) : Math.max(12, country.overall_risk_score / 4))}
                          fillColor={getRiskColor(country.overall_risk_score)}
                          fillOpacity={isFootprint ? 0.95 : isMonitored ? 0.9 : 0.5}
                          stroke={true}
                          weight={isFootprint ? 3 : isMonitored ? 3 : 2}
                          color={isFootprint ? '#FFD700' : isMonitored ? '#4DD0E1' : '#fff'}
                          eventHandlers={{ click: () => navigate(`/countries/${country.country_code}`) }}
                        >
                          <Popup>
                            <div className="p-2">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">{country.flag_emoji}</span>
                                <span className="font-bold text-[#0D3B4F]">{country.country_name}</span>
                                {isFootprint && <Badge className="bg-[#FF9500] text-white text-[9px] px-1.5 py-0">Footprint</Badge>}
                                {isMonitored && !isFootprint && <Badge className="bg-[#00707C] text-white text-[9px] px-1.5 py-0">Monitored</Badge>}
                              </div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm text-[#4E6974]">Risk score:</span>
                                <span className="font-bold" style={{ color: getRiskColor(country.overall_risk_score) }}>{country.overall_risk_score}/100</span>
                              </div>
                              {isFootprint && (
                                <div className="text-xs text-[#4E6974]">
                                  {tradeFootprint[country.country_code].direction === 'source' ? 'Source from' : tradeFootprint[country.country_code].direction === 'sell' ? 'Sell to' : 'Both'} · {(fpVolume || 'N/A').replace('_', '-')} of trade
                                </div>
                              )}
                              <div className="text-xs text-[#4E6974] mt-1">Click to view details</div>
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </CardContent>
              </Card>
            </motion.div>

            {/* Alerts Feed - sorted by user priorities */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="lg:col-span-2">
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-[450px]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F]">Recent alerts</CardTitle>
                    <div className="flex items-center gap-3">
                      {hasFootprint && (
                        <button onClick={() => setFootprintAlerts(!footprintAlerts)}
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${footprintAlerts ? 'bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30' : 'bg-[#F5F5F5] text-[#4E6974] hover:text-[#0D3B4F]'}`}
                          data-testid="footprint-alerts-toggle">
                          <MapPin className="h-3 w-3" />
                          Footprint
                        </button>
                      )}
                      <Link to="/alerts" className="text-[#0097A7] text-sm hover:underline">View all</Link>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[380px] px-6">
                    <div className="space-y-4 pb-4">
                      {displayAlerts.map((alert, index) => (
                        <div 
                          key={alert.id}
                          className="flex gap-3 p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] transition-colors cursor-pointer"
                          data-testid={`alert-item-${index}`}
                          onClick={() => alert.country_code ? navigate(`/countries/${alert.country_code}`) : null}
                        >
                          <Badge className={`${getRiskBadgeClass(alert.severity)} h-fit text-[10px] px-2 py-0.5 font-bold`}>{alert.severity}</Badge>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#0D3B4F] font-medium line-clamp-2">{alert.summary}</p>
                            <div className="flex items-center gap-2 mt-1 text-xs text-[#4E6974]">
                              <span>{alert.country_name}</span>
                              <span>•</span>
                              <span>{formatDateTime(alert.timestamp)}</span>
                              {hasProfile && monitoredCountries.includes(alert.country_code) && (
                                <Badge className="bg-[#E0F7FA] text-[#0097A7] text-[9px] px-1 py-0">Watched</Badge>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-[#4E6974] flex-shrink-0 self-center" />
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F]">Risk by sector</CardTitle>
                    <Link to="/sectors" className="text-[#0097A7] text-sm hover:underline">View all</Link>
                  </div>
                  {audienceLabel && (
                    <p className="text-xs text-[#0097A7] mt-1">Your sector ({audienceLabel}) is highlighted</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]"><Bar data={sectorChartData} options={sectorChartOptions} /></div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
                <CardHeader><CardTitle className="text-lg font-bold text-[#0D3B4F]">Risk trend (12 months)</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-[250px]"><Line data={trendChartData} options={trendChartOptions} plugins={[lastPointLabelPlugin]} /></div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Government-specific: Compliance Scorecard + Reports quick link */}
          {hasProfile && audienceType === 'government' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="gov-compliance-scorecard">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-[#0097A7]" /> Panel risk scorecard
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(() => {
                        const mc = countries.filter(c => monitoredCountries.includes(c.country_code));
                        const low = mc.filter(c => c.overall_risk_score < 40).length;
                        const med = mc.filter(c => c.overall_risk_score >= 40 && c.overall_risk_score < 60).length;
                        const high = mc.filter(c => c.overall_risk_score >= 60).length;
                        return (
                          <>
                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#2E7D32]/5 border border-[#2E7D32]/20">
                              <span className="text-sm text-[#0D3B4F]">Low risk (below 40)</span>
                              <Badge className="bg-[#2E7D32] text-white">{low} countries</Badge>
                            </div>
                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#FF9500]/5 border border-[#FF9500]/20">
                              <span className="text-sm text-[#0D3B4F]">Moderate risk (40-59)</span>
                              <Badge className="bg-[#FF9500] text-white">{med} countries</Badge>
                            </div>
                            <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#FF3B30]/5 border border-[#FF3B30]/20">
                              <span className="text-sm text-[#0D3B4F]">High risk (60+)</span>
                              <Badge className="bg-[#FF3B30] text-white">{high} countries</Badge>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Based on {monitoredCountries.length} monitored supplier panel countries</p>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.84 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="gov-reports-link">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                      <FileText className="h-5 w-5 text-[#0097A7]" /> Reports & briefings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Link to="/reports" className="flex items-center justify-between p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] transition-colors" data-testid="gov-reports-nav">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[#0097A7]" />
                        <span className="text-sm font-medium text-[#0D3B4F]">View all reports</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#4E6974]" />
                    </Link>
                    <Link to="/modern-slavery" className="flex items-center justify-between p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] transition-colors">
                      <div className="flex items-center gap-2">
                        <ShieldAlert className="h-4 w-4 text-[#FF9500]" />
                        <span className="text-sm font-medium text-[#0D3B4F]">Modern slavery risk</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#4E6974]" />
                    </Link>
                    <Link to="/concentration" className="flex items-center justify-between p-3 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] transition-colors">
                      <div className="flex items-center gap-2">
                        <Factory className="h-4 w-4 text-[#4E6974]" />
                        <span className="text-sm font-medium text-[#0D3B4F]">Concentration analysis</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-[#4E6974]" />
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          )}

          {/* Defence-specific: AUKUS Quick Access */}
          {hasProfile && audienceType === 'defence' && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82 }} className="mt-6" data-testid="defence-aukus-quicklink">
              <Card className="bg-gradient-to-r from-[#0D3B4F] to-[#0A2533] rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.08)] border-none">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0097A7]/20 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-[#4DD0E1]" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">AUKUS compliance centre</p>
                        <p className="text-xs text-white/60 mt-0.5">Sovereignty scoring, allied-nation mapping, and compliance attestation</p>
                      </div>
                    </div>
                    <Link to="/aukus" className="px-4 py-2 rounded-lg bg-[#00707C] text-white text-sm font-medium hover:bg-[#005F6A] transition-colors" data-testid="aukus-quick-link">
                      Open AUKUS
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Currency & Freight Row — shown for importers, hidden for govt (they get scorecard above) */}
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 ${audienceType === 'government' ? 'opacity-60' : ''}`}>
            {/* Currency Impact Widget */}
            {currencyPairs.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="currency-widget">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-[#0097A7]" /> Currency impact
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">Live</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="currency-table">
                        <thead>
                          <tr className="text-[#4E6974] text-xs border-b border-[#E0E0E0]">
                            <th className="text-left py-2 font-medium">Pair</th>
                            <th className="text-right py-2 font-medium">Rate</th>
                            <th className="text-right py-2 font-medium">7d</th>
                            <th className="text-right py-2 font-medium">30d</th>
                            <th className="text-right py-2 font-medium w-[72px]">Trend</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currencyPairs.slice(0, 10).map((p) => (
                            <tr key={p.currency_code} className="border-b border-[#F5F5F5] hover:bg-[#F5F5F5] cursor-pointer transition-colors"
                              onClick={() => navigate(`/countries/${p.country_code}`)}>
                              <td className="py-2 font-medium text-[#0D3B4F]">{homeCurrency}/{p.currency_code}</td>
                              <td className="py-2 text-right text-[#0D3B4F] tabular-nums">{p.aud_rate < 100 ? p.aud_rate.toFixed(4) : p.aud_rate.toFixed(1)}</td>
                              <td className={`py-2 text-right tabular-nums font-medium ${p.change_7d_pct > 0 ? 'text-[#FF3B30]' : p.change_7d_pct < 0 ? 'text-[#2E7D32]' : 'text-[#4E6974]'}`}>
                                {p.change_7d_pct > 0 ? '+' : ''}{p.change_7d_pct.toFixed(1)}%
                              </td>
                              <td className={`py-2 text-right tabular-nums font-medium ${p.change_30d_pct > 0 ? 'text-[#FF3B30]' : p.change_30d_pct < 0 ? 'text-[#2E7D32]' : 'text-[#4E6974]'}`}>
                                {p.change_30d_pct > 0 ? '+' : ''}{p.change_30d_pct.toFixed(1)}%
                              </td>
                              <td className="py-1 text-right">
                                {p.sparkline && p.sparkline.length >= 2 ? (
                                  <div className="inline-block w-[60px] h-[24px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                      <LineChart data={p.sparkline}>
                                        <ReLine
                                          type="monotone"
                                          dataKey="rate"
                                          stroke={p.change_30d_pct > 0 ? '#FF3B30' : p.change_30d_pct < 0 ? '#2E7D32' : '#4E6974'}
                                          strokeWidth={1.5}
                                          dot={false}
                                        />
                                      </LineChart>
                                    </ResponsiveContainer>
                                  </div>
                                ) : (
                                  <span className="text-[#BDBDBD] text-xs">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Red = AUD weakening (import costs rising). Green = AUD strengthening.</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Freight Rates Widget */}
            {freightLanes.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.88 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none" data-testid="freight-widget">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        <Ship className="h-5 w-5 text-[#0097A7]" /> Freight rates
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">Weekly</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {freightLanes.slice(0, 6).map((lane, i) => {
                        const rate = lane.current_rate_usd;
                        const avg = lane.avg_12m_rate_usd;
                        const aboveAvg = rate > avg;
                        return (
                          <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[#F5F5F5] hover:bg-[#EEEEEE] cursor-pointer transition-colors"
                            onClick={() => navigate(`/countries/${lane.country_code}`)}
                            data-testid={`freight-lane-${i}`}>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-[#0D3B4F]">{lane.origin} → Australia</p>
                              <p className="text-[10px] text-[#4E6974]">12m avg: ${avg?.toLocaleString()}/FEU</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-[#0D3B4F]">${rate?.toLocaleString()}<span className="text-xs font-normal text-[#4E6974]">/FEU</span></p>
                              <p className={`text-xs font-medium ${lane.wow_change_pct > 0 ? 'text-[#FF3B30]' : 'text-[#2E7D32]'}`}>
                                WoW: {lane.wow_change_pct > 0 ? '+' : ''}{lane.wow_change_pct?.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Per FEU (40ft container). Source: industry benchmarks + World Bank.</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Briefings */}
          {/* New Data Sources Row: Trade Snapshot + Port Status + Sanctions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* ABS Trade Snapshot */}
            {tradeSnapshot && tradeSnapshot.supplier_count > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.91 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="trade-snapshot-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        <Package className="h-5 w-5 text-[#0097A7]" /> Australian trade snapshot
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">ABS</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-[#4E6974] mb-3">
                      Your supplier countries account for <span className="font-bold text-[#0D3B4F]">{tradeSnapshot.supplier_countries_import_pct}%</span> of Australia's total merchandise imports.
                    </p>
                    <div className="space-y-2">
                      {tradeSnapshot.countries?.slice(0, 5).map((c, i) => (
                        <div key={c.country_code} className="flex items-center justify-between text-xs p-1.5 rounded bg-[#F5F5F5]">
                          <span className="text-[#0D3B4F] font-medium">{c.country_name}</span>
                          <span className="text-[#4E6974] tabular-nums">${c.imports_aud > 0 ? (c.imports_aud / 1e9).toFixed(1) + 'B' : 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: Australian Bureau of Statistics merchandise trade data</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Port Congestion Widget */}
            {portStatus.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.93 }}>
                <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full" data-testid="port-status-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        <Anchor className="h-5 w-5 text-[#0097A7]" /> Shipping and port status
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">Live</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-1.5">
                      {dashboardPorts.map((port) => {
                        const levelColor = port.congestion_level === 'Severe' ? '#FF3B30' : port.congestion_level === 'High' ? '#FF9500' : port.congestion_level === 'Moderate' ? '#FFCC02' : '#2E7D32';
                        return (
                          <div key={port.port_id} className="flex items-center justify-between text-xs p-1.5 rounded bg-[#F5F5F5] hover:bg-[#EEEEEE] cursor-pointer transition-colors"
                            onClick={() => navigate(`/countries/${port.country_code}`)} data-testid={`port-${port.port_id}`}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: levelColor }} />
                              <span className="text-[#0D3B4F] font-medium">{port.port_name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-[#4E6974] tabular-nums">{port.wait_days}d wait</span>
                              <span className="font-medium" style={{ color: levelColor }}>{port.congestion_level}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {portStatus.filter(p => p.congestion_level === 'High' || p.congestion_level === 'Severe').length > 0 && (
                      <p className="text-xs text-[#FF9500] mt-3 font-medium">
                        {portStatus.filter(p => p.congestion_level === 'High' || p.congestion_level === 'Severe').length} port(s) experiencing elevated congestion
                      </p>
                    )}
                    <p className="text-[10px] text-[#4E6974] mt-2 italic">Source: World Bank CPPI + freight data model</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Sanctions Compliance Card */}
            {sanctionsDashboard && sanctionsDashboard.total > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }}>
                <Card className={`rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none h-full ${sanctionsDashboard.flagged > 0 ? 'bg-[#FFF5F5] border border-[#FF3B30]/20' : 'bg-white'}`} data-testid="sanctions-compliance-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2">
                        {sanctionsDashboard.flagged > 0 ? <ShieldAlert className="h-5 w-5 text-[#FF3B30]" /> : <ShieldCheck className="h-5 w-5 text-[#2E7D32]" />}
                        Sanctions screening
                      </CardTitle>
                      <Badge variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-[10px]">Multi-regime</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {sanctionsDashboard.flagged > 0 ? (
                      <div className="p-3 rounded-lg bg-[#FF3B30]/10 border border-[#FF3B30]/20 mb-3">
                        <p className="text-sm font-bold text-[#FF3B30]">{sanctionsDashboard.flagged} supplier(s) require sanctions review</p>
                        <Link to="/suppliers" className="text-xs text-[#FF3B30] hover:underline mt-1 inline-block">View details</Link>
                      </div>
                    ) : (
                      <div className="p-3 rounded-lg bg-[#2E7D32]/10 border border-[#2E7D32]/20 mb-3">
                        <p className="text-sm font-bold text-[#2E7D32]">All {sanctionsDashboard.clear} suppliers passed sanctions screening</p>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-[#F5F5F5]">
                        <p className="text-lg font-bold text-[#0D3B4F]">{sanctionsDashboard.total}</p>
                        <p className="text-[10px] text-[#4E6974]">Total</p>
                      </div>
                      <div className="p-2 rounded bg-[#F5F5F5]">
                        <p className="text-lg font-bold text-[#2E7D32]">{sanctionsDashboard.clear}</p>
                        <p className="text-[10px] text-[#4E6974]">Clear</p>
                      </div>
                      <div className="p-2 rounded bg-[#F5F5F5]">
                        <p className="text-lg font-bold text-[#4E6974]">{sanctionsDashboard.not_screened}</p>
                        <p className="text-[10px] text-[#4E6974]">Pending</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-[#4E6974] mt-3 italic">Source: DFAT, OFAC, EU, BIS, OFSI consolidated sanctions lists</p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>

          {/* Briefings (original) */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="mt-6">
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Recent risk briefings</CardTitle>
                  <Link to="/reports" className="text-[#0097A7] text-sm hover:underline">View all</Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {briefings.map((briefing, index) => (
                    <div 
                      key={briefing.id}
                      className="p-4 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] hover:shadow-md transition-all cursor-pointer group"
                      data-testid={`briefing-card-${index}`}
                      onClick={() => navigate(`/briefings/${briefing.id}`)}
                    >
                      <h4 className="font-semibold text-[#0D3B4F] text-sm mb-2 line-clamp-2 group-hover:text-[#0097A7] transition-colors">{briefing.title}</h4>
                      <p className="text-[#4E6974] text-xs line-clamp-2 mb-3">{briefing.summary}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          {briefing.country_tags?.map(tag => (
                            <Badge key={tag} variant="secondary" className="text-[10px] bg-[#F5F5F5] text-[#4E6974]">{tag}</Badge>
                          ))}
                        </div>
                        <span className="text-[10px] text-[#4E6974]">{formatDateTime(briefing.created_at)}</span>
                      </div>
                      <div className="mt-3 flex items-center text-[#0097A7] text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        Read full briefing <ChevronRight className="h-3 w-3 ml-1" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
